import * as HTMLElementCreator from '../../../libs/HTML/HTMLElementCreator';
import type { EditorPanel } from './types';
import type { TileColor } from '../../../game/boardMVC/TileColor';
import type { BlockerType } from '../../../game/boardMVC/types';

interface EditorItemSelectorConfig {
  readonly buttons: EditorItemSelectorButtonConfig[];
  readonly onItemSelected?: (color: AcceptableItemValues) => void;
}

export interface EditorItemSelectorButtonConfig {
  value: AcceptableItemValues;
  color?: string;
  borderColor: string;
  image?: string;
}

interface ItemButton {
  element: HTMLElement;
  config: EditorItemSelectorButtonConfig;
}

type AcceptableItemValues = TileColor | BlockerType | null;

export class EditorItemSelector implements EditorPanel {
  private static readonly itemButtonSize = '40px';

  private static readonly wheelEventThrottleMs = 50;

  public readonly element: HTMLElement;

  private readonly buttons: ItemButton[];

  #selectedValue: AcceptableItemValues = null;

  private lastWheelEventAt = 0;

  constructor(private readonly config: EditorItemSelectorConfig) {
    this.element = HTMLElementCreator.createDiv({
      parentElement: undefined,
      style: {
        position: 'fixed',
        top: '50px',
        right: '5px',
        padding: '2px 2px 2px 2px',
        color: 'black',
        maxWidth: EditorItemSelector.itemButtonSize,
        pointerEvents: 'auto',
        backgroundColor: 'black',
      },
    });

    this.buttons = config.buttons.map((buttonConfig) => this.createItemButton(buttonConfig));

    this.updateButtonHighlights();
  }

  public destroy(): void {
    this.element.remove();
    document.removeEventListener('wheel', this.onMouseWheel);
  }

  public focus(): void {
    this.element.style.visibility = 'visible';
    document.addEventListener('wheel', this.onMouseWheel);
  }

  public blur(): void {
    this.element.style.visibility = 'hidden';
    document.removeEventListener('wheel', this.onMouseWheel);
  }

  private createItemButton(config: EditorItemSelectorButtonConfig): ItemButton {
    const { value, color = 'white', image } = config;
    const element = HTMLElementCreator.createButton({
      parentElement: this.element,
      labelText: '',
      onClick: () => {
        this.onItemButtonSelected(value);
      },
      style: {
        backgroundColor: color,
        height: EditorItemSelector.itemButtonSize,
        width: EditorItemSelector.itemButtonSize,
        borderWidth: '0px',
        borderStyle: 'dashed',
        borderColor: config.borderColor,

        color: config.borderColor,
        userSelect: 'none',
        marginTop: '2px',
        padding: '3px 3px 3px 3px',
      },
    });
    if (config.image) {
      element.style.backgroundImage = `url(${image})`;
      element.style.backgroundSize = 'contain';
    }
    return { element, config };
  }

  private onItemButtonSelected(value: AcceptableItemValues): void {
    const { onItemSelected } = this.config;
    this.#selectedValue = value;
    this.updateButtonHighlights();
    onItemSelected?.(this.#selectedValue);
  }

  private updateButtonHighlights(): void {
    this.buttons.forEach(({ element, config }) => {
      if (config.value === this.#selectedValue) {
        element.style.borderWidth = '2px';
        element.innerHTML = '+';
      } else {
        element.style.borderWidth = '0px';
        element.innerHTML = '';
      }
    });
  }

  public get selectedValue(): AcceptableItemValues {
    return this.#selectedValue;
  }

  public setSelectionByIndex(index: number): void {
    const button = this.buttons.at(index);
    if (button) {
      this.onItemButtonSelected(button.config.value);
    }
  }

  private readonly onMouseWheel = (e: WheelEvent): void => {
    if (Date.now() - this.lastWheelEventAt < EditorItemSelector.wheelEventThrottleMs) return;
    this.lastWheelEventAt = Date.now();

    const sign = Math.sign(e.deltaY);
    const selectedIndex = this.buttons.findIndex((button) => button.config.value === this.#selectedValue);

    let nextIndex = selectedIndex + sign;
    if (nextIndex > this.buttons.length - 1) nextIndex = 0;
    else if (nextIndex < 0) nextIndex = this.buttons.length - 1;
    this.onItemButtonSelected(this.buttons[nextIndex].config.value);
  };
}
